/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * AWS Insights Widget Definitions
 *
 * Defines all widgets for the AWS Insights dashboard template.
 * Each widget specifies its data transformation from the AWS Insights source data.
 */

import {
  createMetricWidget,
  createChartWidget,
  createListWidget,
  WIDGET_CATEGORIES,
  WIDGET_ICONS,
  DATA_SOURCES,
  WidgetTemplate,
} from '../widgetTemplates';

const {
  FiDollarSign,
  FiTrendingUp,
  FiClipboard,
  FiAward,
  FiAlertTriangle,
  FiCheckCircle,
  FiPieChart,
  FiClock,
  FiBarChart2,
} = WIDGET_ICONS;

/**
 * Total Infrastructure Spend Widget
 */
export const totalInfrastructureSpendWidget = createMetricWidget({
  id: 'total-infrastructure-spend-combined',
  name: 'Total Infrastructure Spend',
  description: 'Combined spent + estimated infrastructure costs',
  icon: FiDollarSign,
  category: WIDGET_CATEGORIES.AWS_INSIGHTS,
  dataSource: DATA_SOURCES.AWS_INSIGHTS,
  respectsClientFilter: true,
  refreshRate: 60000,
  getData: (sourceData) => ({
    value: sourceData?.infrastructureSpend?.total_combined || 0,
    label: 'Total Infrastructure',
    format: 'currency',
    color: 'blue',
    subtitle: `$${(sourceData?.infrastructureSpend?.total_spent || 0).toLocaleString()} spent + $${(sourceData?.infrastructureSpend?.total_estimated || 0).toLocaleString()} estimated`,
    link: '/hub/funding',
  }),
});

/**
 * Infrastructure Budget Utilization Widget
 */
export const infrastructureBudgetUtilizationWidget = createMetricWidget({
  id: 'infrastructure-budget-utilization',
  name: 'Budget Utilization',
  description: 'Infrastructure budget utilization percentage',
  icon: FiTrendingUp,
  category: WIDGET_CATEGORIES.AWS_INSIGHTS,
  dataSource: DATA_SOURCES.AWS_INSIGHTS,
  respectsClientFilter: true,
  refreshRate: 60000,
  getData: (sourceData) => ({
    value: sourceData?.infrastructureSpend?.utilization_pct || 0,
    label: 'Budget Utilization',
    format: 'percentage',
    color: (sourceData?.infrastructureSpend?.utilization_pct || 0) > 90 ? 'red' : 'green',
    link: '/hub/funding',
  }),
});

/**
 * Active Funding Requests Widget
 */
export const fundingActiveRequestsWidget = createMetricWidget({
  id: 'funding-active-requests',
  name: 'Active Funding Requests',
  description: 'Number of active funding requests',
  icon: FiClipboard,
  category: WIDGET_CATEGORIES.AWS_INSIGHTS,
  dataSource: DATA_SOURCES.AWS_INSIGHTS,
  respectsClientFilter: true,
  refreshRate: 60000,
  getData: (sourceData) => ({
    value: sourceData?.fundingSummary?.active_count || 0,
    label: 'Active Requests',
    format: 'number',
    color: 'cyan',
    link: '/hub/funding',
  }),
});

/**
 * Applied Credits Widget
 */
export const fundingTotalCreditsWidget = createMetricWidget({
  id: 'funding-total-credits',
  name: 'Applied Credits',
  description: 'Total applied AWS credits (all active credit codes)',
  icon: FiAward,
  category: WIDGET_CATEGORIES.AWS_INSIGHTS,
  dataSource: DATA_SOURCES.AWS_INSIGHTS,
  respectsClientFilter: true,
  refreshRate: 60000,
  getData: (sourceData) => ({
    value: sourceData?.creditsOverview?.total_available || 0,
    label: 'Applied Credits',
    format: 'currency',
    color: 'green',
    link: '/hub/funding',
  }),
});

/**
 * Credits Expiring Soon Widget
 */
export const fundingCreditsExpiringWidget = createMetricWidget({
  id: 'funding-credits-expiring',
  name: 'Credits Expiring Soon',
  description: 'Credits expiring within 30 days',
  icon: FiAlertTriangle,
  category: WIDGET_CATEGORIES.AWS_INSIGHTS,
  dataSource: DATA_SOURCES.AWS_INSIGHTS,
  respectsClientFilter: true,
  refreshRate: 60000,
  getData: (sourceData) => {
    const expiringAmount = sourceData?.creditsOverview?.expiring_soon || 0;
    return {
      value: expiringAmount,
      label: 'Expiring Soon',
      format: 'currency',
      color: expiringAmount > 0 ? 'orange' : 'gray',  // Orange only if credits are actually expiring
      link: '/hub/funding',
    };
  },
});

/**
 * Funding Approval Rate Widget
 */
export const fundingApprovalRateWidget = createMetricWidget({
  id: 'funding-approval-rate',
  name: 'Approval Rate',
  description: 'Funding request approval rate',
  icon: FiCheckCircle,
  category: WIDGET_CATEGORIES.AWS_INSIGHTS,
  dataSource: DATA_SOURCES.AWS_INSIGHTS,
  respectsClientFilter: true,
  refreshRate: 60000,
  getData: (sourceData) => ({
    value: sourceData?.fundingSummary?.approval_rate || 0,
    label: 'Approval Rate',
    format: 'percentage',
    color: 'green',
    link: '/hub/funding',
  }),
});

/**
 * Funding by Program Widget
 */
export const fundingByProgramWidget = createChartWidget({
  id: 'funding-by-program',
  name: 'Funding by Program',
  description: 'Distribution of funding across AWS programs',
  icon: FiPieChart,
  category: WIDGET_CATEGORIES.AWS_INSIGHTS,
  dataSource: DATA_SOURCES.AWS_INSIGHTS,
  respectsClientFilter: true,
  refreshRate: 60000,
  getData: (sourceData) => {
    const programs = sourceData?.fundingByProgram?.programs || [];
    return {
      data: programs.map((p: Record<string, unknown>) => ({
        name: p.name || 'Unknown',
        value: p.total_amount || p.count || 0,
      })),
      chartType: 'pie',
      dataKey: 'value',
      link: '/hub/funding',
    };
  },
});

/**
 * Funding Requests Timeline Widget
 */
export const fundingRequestsTimelineWidget = createChartWidget({
  id: 'funding-requests-timeline',
  name: 'Funding Timeline',
  description: 'Funding requests over time',
  icon: FiClock,
  category: WIDGET_CATEGORIES.AWS_INSIGHTS,
  dataSource: DATA_SOURCES.AWS_INSIGHTS,
  respectsClientFilter: true,
  refreshRate: 60000,
  getData: (sourceData) => ({
    data: sourceData?.fundingTimeline?.timeline || [],
    chartType: 'line',
    xKey: 'month',
    yKey: 'total_amount',
    link: '/hub/funding',
  }),
});

/**
 * Credit Allocation Widget
 */
export const creditsByClientWidget = createChartWidget({
  id: 'credits-by-client',
  name: 'Credit Allocation',
  description: 'Credit allocation breakdown for selected client',
  icon: FiBarChart2,
  category: WIDGET_CATEGORIES.AWS_INSIGHTS,
  dataSource: DATA_SOURCES.AWS_INSIGHTS,
  respectsClientFilter: true,
  refreshRate: 60000,
  getData: (sourceData) => ({
    data: sourceData?.creditsByClient?.clients || [],
    chartType: 'bar',
    xKey: 'client_name',
    yKey: 'total_credits',
    link: '/hub/funding',
  }),
});

/**
 * Credit Expiration Timeline Widget
 */
export const creditExpirationTimelineWidget = createChartWidget({
  id: 'credit-expiration-timeline',
  name: 'Credit Expiration',
  description: 'Credits expiring over next 12 months',
  icon: FiClock,
  category: WIDGET_CATEGORIES.AWS_INSIGHTS,
  dataSource: DATA_SOURCES.AWS_INSIGHTS,
  respectsClientFilter: true,
  refreshRate: 60000,
  getData: (sourceData) => ({
    data: sourceData?.creditsOverview?.expiration_timeline || [],
    chartType: 'line',
    xKey: 'month',
    yKey: 'amount',
    link: '/hub/funding',
  }),
});

/**
 * Recent Funding Requests Widget
 */
export const fundingRecentRequestsWidget = createListWidget({
  id: 'funding-recent-requests',
  name: 'Recent Funding Requests',
  description: 'Latest funding request submissions',
  icon: FiClipboard,
  category: WIDGET_CATEGORIES.AWS_INSIGHTS,
  dataSource: DATA_SOURCES.AWS_INSIGHTS,
  respectsClientFilter: true,
  refreshRate: 60000,
  getData: (sourceData) => {
    const requests = sourceData?.fundingSummary?.recent_requests || [];
    return {
      items: requests.map((req: Record<string, unknown>) => ({
        title: req.name || req.program_name || 'Funding Request',
        subtitle: req.status || '',
        value: req.requested_amount ? `$${req.requested_amount.toLocaleString()}` : '',
        id: req.id || req.application_id,
      })),
    };
  },
});

/**
 * Projects At Risk Widget
 */
export const projectsNeedingAttentionWidget = createListWidget({
  id: 'projects-needing-attention',
  name: 'Projects At Risk',
  description: 'Projects with yellow or red health status',
  icon: FiAlertTriangle,
  category: WIDGET_CATEGORIES.AWS_INSIGHTS,
  dataSource: DATA_SOURCES.AWS_INSIGHTS,
  respectsClientFilter: true,
  refreshRate: 60000,
  getData: (sourceData) => {
    const projects = sourceData?.projectsAtRisk?.projects || [];
    return {
      items: projects.map((project: Record<string, unknown>) => ({
        title: project.name || 'Project',
        subtitle: project.client_name || '',
        value: project.health_status || 'unknown',
        status: project.health_status,
        id: project.id || project.project_id,
      })),
    };
  },
});

// Export all widgets as an array
export const AWS_INSIGHTS_WIDGETS = [
  totalInfrastructureSpendWidget,
  infrastructureBudgetUtilizationWidget,
  fundingActiveRequestsWidget,
  fundingTotalCreditsWidget,
  fundingCreditsExpiringWidget,
  fundingApprovalRateWidget,
  fundingByProgramWidget,
  fundingRequestsTimelineWidget,
  creditsByClientWidget,
  creditExpirationTimelineWidget,
  fundingRecentRequestsWidget,
  projectsNeedingAttentionWidget,
];

// Export as a map for quick lookup
export const AWS_INSIGHTS_WIDGETS_MAP = AWS_INSIGHTS_WIDGETS.reduce((map: Record<string, WidgetTemplate>, widget) => {
  map[widget.id] = widget;
  return map;
}, {} as Record<string, WidgetTemplate>);
